import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Home, Menu, X, CalendarCheck, LogOut, LogIn } from "lucide-react";
import { BRAND, TAGLINE, BOOKING_URL } from "@/config";
import { useAuth } from "@/lib/AuthContext";

const links = [
  { to: "/mon-parcours", label: "Mon parcours", testid: "nav-parcours-button" },
  { to: "/diagnostic", label: "Diagnostic 5 min", testid: "nav-diagnostic-button" },
  { to: "/formations", label: "Formations", testid: "nav-formations-button" },
  { to: "/calculatrices", label: "Calculatrices", testid: "nav-calculatrices-button" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFDFD]/85 border-b border-[#012141]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3.5 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#012141] text-[#F5C64E] shadow-lg group-hover:scale-105 transition-transform">
            <Home size={20} strokeWidth={2.4} fill="#F5C64E" />
          </span>
          <span className="leading-tight">
            <span className="block font-heading font-bold text-[#012141] text-[15px] sm:text-base">{BRAND}</span>
            <span className="block text-[11px] text-slate-500 font-medium">{TAGLINE}</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => {
            const active = location.pathname.startsWith(l.to);
            return (
              <Link key={l.to} to={l.to} data-testid={l.testid}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "bg-[#012141]/8 text-[#012141]" : "text-slate-600 hover:bg-[#012141]/5 hover:text-[#012141]"}`}>
                {l.label}
              </Link>
            );
          })}
          {user ? (
            <>
              <Link to="/admin" data-testid="nav-admin-button" className="px-4 py-2 rounded-full text-sm font-semibold text-[#012141] hover:bg-[#012141]/5">Admin</Link>
              <button data-testid="nav-logout" onClick={logout} className="px-3 py-2 rounded-full text-sm text-slate-500 hover:bg-slate-100 inline-flex items-center gap-1.5"><LogOut size={15} /></button>
            </>
          ) : (
            <Link to="/admin/login" data-testid="nav-pro-login-button" className="px-4 py-2 rounded-full text-sm font-semibold text-[#012141] hover:bg-[#012141]/5 inline-flex items-center gap-1.5"><LogIn size={15} /> Espace pro</Link>
          )}
          <a href={BOOKING_URL} target="_blank" rel="noreferrer" data-testid="header-booking-cta"
            className="btn-gold ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C89A3E] text-[#012141] text-sm font-bold hover:brightness-105 transition-[filter] shadow-[0_8px_20px_-8px_rgba(199,154,58,0.7)]">
            <CalendarCheck size={16} /> Rendez-vous
          </a>
        </nav>

        <button data-testid="mobile-menu-toggle" className="md:hidden grid place-items-center w-10 h-10 rounded-xl bg-[#012141]/8 text-[#012141]" onClick={() => setOpen((v) => !v)} aria-label="Menu">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#012141]/10 bg-white px-4 py-3 space-y-1">
          {links.map((l) => (
            <button key={l.to} data-testid={`mobile-${l.testid}`} onClick={() => { navigate(l.to); setOpen(false); }}
              className="w-full text-left px-4 py-3 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100">{l.label}</button>
          ))}
          {user
            ? <button onClick={() => { navigate("/admin"); setOpen(false); }} className="w-full text-left px-4 py-3 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100">Admin</button>
            : <button data-testid="mobile-nav-pro-login-button" onClick={() => { navigate("/admin/login"); setOpen(false); }} className="w-full text-left px-4 py-3 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1.5"><LogIn size={15} /> Espace pro (connexion)</button>}
          <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="block px-4 py-3 rounded-xl text-sm font-bold text-[#012141] text-center bg-[#C89A3E]">Prendre un rendez-vous</a>
        </div>
      )}
    </header>
  );
};
